<template>
  <!-- Add to cart button: used for collection products -->
  <AddToCartButton
    v-if="configuratorStore.isCollectionProduct"
    :loading="buttonLoading"
    @on-click="onAddToCartButtonClick"
  />

  <!-- Proceed button: when mediaclip is already initialized (photo has been uploaded already) -->
  <ProceedButton
    v-else-if="configuratorStore.mediaclipLaunched"
    :loading="configuratorStore.showFullPageLoader"
    @on-click="startConfigurator"
  />

  <!-- Upload button: used for upload configurator (upload customer photo) -->
  <UploadButton v-else-if="configuratorStore.isUploadConfigurator" />

  <!-- Start button: no need to upload photo, just start configurator -->
  <StartButton
    v-else
    :loading="configuratorStore.showFullPageLoader"
    @on-click="startConfigurator"
  />
</template>

<script setup lang="ts">
import AddToCartButton from '@/components/Buttons/AddToCartButton.vue';
import ProceedButton from '@/components/Buttons/ProceedButton.vue';
import StartButton from '@/components/Buttons/StartButton.vue';
import UploadButton from '@/components/Buttons/UploadButton.vue';
import { computed, type ComputedRef, ref, type Ref } from 'vue';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore.ts';
import { useAddToCart } from '@/composables/UseAddToCart.ts';
import { useExtraOptions } from '@/composables/UseExtraOptions.ts';
import { watchOnce } from '@vueuse/core';
import { useExtraOptionsStore } from '@/stores/ExtraOptionsStore.ts';
import I18n from '@/i18n.ts';
import { useMessagesStore } from '@/stores/MessagesStore.ts';
import { useMediaclipCommunication } from '@/composables/UseMediaclipCommunication.ts';
import { useSizeStore } from '@/stores/Configurator/SizeStore.ts';

const messagesStore = useMessagesStore();
const configuratorStore = useConfiguratorStore();
const addToCart = useAddToCart();
const sizeStore = useSizeStore();
const extraOptions = useExtraOptions();
const extraOptionsStore = useExtraOptionsStore();
const mediaclipCommunicationComposable = useMediaclipCommunication();

const retrievedAllCollectionData: Ref<boolean> = ref(false);

const collectionLoading: ComputedRef<boolean> = computed(
  () =>
    !retrievedAllCollectionData.value &&
    (addToCart.isAwaitingRedirect.value ||
      addToCart.addCollectionProductToCartRequest.isLoading ||
      extraOptions.request.isLoading)
);

const buttonLoading: ComputedRef<boolean> = computed(() => {
  if (configuratorStore.isCollectionProduct) {
    return collectionLoading.value;
  }

  return true;
});

function onAddToCartButtonClick(): void {
  retrievedAllCollectionData.value = false;

  if (collectionLoading.value) {
    watchOnce(collectionLoading, () => {
      onAddToCartButtonClick();
    });
    return;
  }

  // Open modal and lock background scrolling
  if (extraOptionsStore.productOptionsCategories.length > 0) {
    configuratorStore.extraOptionsModalOpen = true;
    return;
  }

  addToCart.addCollectionProductToCart(sizeStore.selectedSizeValue!);
}

async function startConfigurator(): Promise<void> {
  if (
    configuratorStore.isLetterCollageConfigurator &&
    !configuratorStore.mediaclipUserInputText &&
    !configuratorStore.mediaclipUserText
  ) {
    messagesStore.dispatchMessage(
      'error',
      I18n.global.t('pages.product_detail_page.user_text.error.empty'),
      8000
    );
    return;
  }

  configuratorStore.showFullPageLoader = true;

  try {
    await mediaclipCommunicationComposable.initMediaclip();
  } catch {
    messagesStore.dispatchMessage(
      'error',
      I18n.global.t('messages.error.upload_button.upload_error'),
      8000
    );
    configuratorStore.showFullPageLoader = false;
    return;
  }

  configuratorStore.setConfiguratorStep();
  configuratorStore.showFullPageLoader = false;

  mediaclipCommunicationComposable.setIdentifier().then(() => {});
}
</script>
